import type { Entity, Helper, HelperResult } from '@openclassrooms/sdk-web';

import { clientApi } from '../../configuration';
import getUseGet from './getUseGet';
import type { UseFindParams } from './getUseFind';
import getUseFind from './getUseFind';
import getUseCreate from './getUseCreate';
import getUseUpdate from './getUseUpdate';
import getUseDelete from './getUseDelete';
import { addDependentEntities } from './dependentEntities';

interface ParentEntity {
  name: string;
  id: number | string;
}

interface BaseParams {
  id?: string | number;
  [key: string]: any;
}

interface ParentEntityArray extends Array<ParentEntity> {}

interface GetCRUDHooksParams<
  EntityType,
  GetRequestParams,
  FindRequestParams,
  CreateRequestParams,
  UpdateRequestParams,
  RemoveRequestParams,
  CreateRequestBody,
  UpdateRequestBody,
  CreateResponseBody,
  UpdateResponseBody
> {
  entityName: string;
  sharedCacheEntities?: string[];
  dependentEntities?: string[];
  parentEntity?: ParentEntity | ParentEntityArray;
  withPermissions?: boolean;
  withLinks?: boolean;
  getHelper?: () => Helper<EntityType>;
  get?: (params: GetRequestParams) => HelperResult<EntityType>;
  find?: (params: FindRequestParams) => HelperResult<EntityType[]>;
  create?: (
    newEntity: CreateRequestBody,
    params?: CreateRequestParams
  ) => HelperResult<CreateResponseBody>;
  update?: (
    mutatedData: UpdateRequestBody,
    params: UpdateRequestParams
  ) => HelperResult<UpdateResponseBody>;
  remove?: (params: RemoveRequestParams) => HelperResult<void>;
}

export default function getCRUDHooks<
  EntityType extends Entity = Entity,
  GetRequestParams extends BaseParams = BaseParams,
  FindRequestParams extends UseFindParams = UseFindParams,
  CreateRequestParams extends BaseParams = BaseParams,
  UpdateRequestParams extends BaseParams = BaseParams,
  RemoveRequestParams extends BaseParams = BaseParams,
  CreateRequestBody extends Partial<Entity> = Partial<EntityType>,
  UpdateRequestBody extends Partial<Entity> = Partial<EntityType>,
  CreateResponseBody extends Partial<Entity> = Partial<EntityType>,
  UpdateResponseBody extends Partial<Entity> = Partial<EntityType>,
  GetRequestOptions extends BaseParams = BaseParams
>({
  entityName,
  parentEntity,
  withPermissions,
  withLinks,
  sharedCacheEntities,
  dependentEntities,
  getHelper = () => {
    const dynamicHelper = clientApi
      .getOCClient()
      .getDynamicHelper<EntityType>(entityName, parentEntity);
    return parentEntity ? dynamicHelper.id(parentEntity.id)[entityName] : dynamicHelper;
  },
  get = ({ id }) => getHelper().get(id as string | number),
  find = ({ filters }) => getHelper().find(filters),
  create = (newEntity) => getHelper().create(newEntity),
  update = (mutatedData, { id }) => getHelper().partialUpdate(id as string | number, mutatedData),
  remove = ({ id }) => getHelper().delete(id as string | number),
}: GetCRUDHooksParams<
  EntityType,
  GetRequestParams,
  FindRequestParams,
  CreateRequestParams,
  UpdateRequestParams,
  RemoveRequestParams,
  CreateRequestBody,
  UpdateRequestBody,
  CreateResponseBody,
  UpdateResponseBody
>) {
  const entityNameList = `${entityName}List`;

  if (dependentEntities) {
    addDependentEntities(entityName, dependentEntities);
  }

  return {
    useGet: getUseGet<GetRequestParams, EntityType, GetRequestOptions>(
      get,
      entityName,
      withPermissions,
      withLinks
    ),
    useFind: getUseFind<FindRequestParams, EntityType>(find, entityNameList),
    useCreate: getUseCreate<CreateRequestParams, EntityType, CreateRequestBody, CreateResponseBody>(
      create,
      entityName,
      entityNameList,
      sharedCacheEntities
    ),
    useUpdate: getUseUpdate<UpdateRequestParams, EntityType, UpdateRequestBody, UpdateResponseBody>(
      update,
      entityName,
      entityNameList,
      sharedCacheEntities
    ),
    useDelete: getUseDelete<RemoveRequestParams>(
      remove,
      entityName,
      entityNameList,
      sharedCacheEntities
    ),
  };
}
